import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { emailAddress, password_input, schemas } from "utilities/formSchemas";
import PasswordChecker from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { useEffect, useState } from "react";
import * as Constant from "utilities/Constant";
import homeGraphic from "assets/images/HomeGraphic.png";
import "./registration.scss";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useDispatch, useSelector } from "react-redux";
import {
  SetPasswordRequest,
  useGetDecryptEmailQuery,
  useGetUserDetailsQuery,
  useSetPasswordWithoutDeviceAuthMutation,
} from "pages/registration/registrationService";
import { getEmailAddress, getNhDealApplicationId, setCredentials, setUserId } from "features/authSlice";
import { ErrorType } from "utilities/error";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { setFSUserProperties } from "utilities/full-story";
import { Consent } from "components/consent/Consent";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import { setAuthDeviceType, setResendCodeDeviceId } from "features/mfaSlice";
import Footer from "components/footer/Footer";

export const formSchema = z.object({
  email: emailAddress,
  phone: schemas.phone.optional(),
  password_input,
});

export default function ExternalRegistration() {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  const [error, setError] = useState(0);
  const [hasPwdError, setHasPwdError] = useState(false);
  const isDesktop = useDesktopChecker();
  const nhDealApplicationIdFromStore = useSelector(getNhDealApplicationId);
  const emailFromStore = useSelector(getEmailAddress);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [passwordRequestFailureMessage, setPasswordRequestFailureMessage] = useState("");
  const [searchParams] = useSearchParams();
  const encryptedUser = searchParams.get("t") ?? "";
  const [emailAddressState, setEmailAddressState] = useState<string>(encryptedUser ? "" : emailFromStore);
  const [nhDealApplicationId, setNhDealApplicationId] = useState<string>("");

  const {
    data: decryptedUserDetails,
    isLoading: isDecryptLoading,
    isSuccess: isDecryptSuccess,
  } = useGetDecryptEmailQuery(encryptedUser, {
    skip: !encryptedUser,
  });

  const {
    data: isPhoneRequired,
    isSuccess: isUserDetailsSuccess,
    isLoading: isUserDetailsLoading,
    isFetching: isPhoneNumberFetching,
  } = useGetUserDetailsQuery(emailAddressState || "", {
    skip: !emailAddressState,
  });

  useEffect(() => {
    if (!isDecryptLoading && isDecryptSuccess) {
      const { ex, em, nhDealAppId } = decryptedUserDetails;
      setFSUserProperties(em);
      if (ex) {
        navigate("/", { replace: true });
      } else {
        setValue("email", em);
        setEmailAddressState(em);
        setNhDealApplicationId(nhDealAppId);
      }
    }
  }, [isDecryptSuccess, isDecryptLoading]);

  useEffect(() => {
    if (!encryptedUser && emailFromStore) {
      setValue("email", emailFromStore);
      setEmailAddressState(emailFromStore);
      setNhDealApplicationId(nhDealApplicationIdFromStore);
    }
  }, [emailFromStore]);

  useEffect(() => {
    if (!encryptedUser && !emailFromStore) {
      navigate("/", { replace: true });
    }
  }, [encryptedUser, emailFromStore]);

  const [sendRequest, { data, isSuccess, isLoading: isRegistrationLoading }] = useSetPasswordWithoutDeviceAuthMutation();

  const onSubmit = (externalRegisterData: SetPasswordRequest) => {
    if (!hasErrors && !hasPwdError) {
      sendRequest(externalRegisterData)
        .unwrap()
        .catch((error: ErrorType) => {
          if (error.status == 409) {
            navigate("/", { replace: true });
          } else if (error.status === 400 && error.data?.code === Constant.ErrorCodeInvalidMFA) {
            setPasswordRequestFailureMessage("Enter Valid Cell Phone Number");
          } else {
            setError(500);
          }
        });
    }
  };

  const getLastNameLowercase = (fullName: string | undefined) => {
    if (!fullName) return "";
    return fullName.split(" ").at(-1)?.toLowerCase() ?? "";
  };

  useEffect(() => {
    if (isSuccess && data) {
      setError(0);
      if (data.isMfaSkipEnabled) {
        dispatch(
          setCredentials({
            name: data?.name ?? "",
            accessToken: data?.accessToken ?? "",
            id: data?.id ?? "",
            refreshToken: data?.refreshToken ?? "",
            version: data?.version ?? "",
            status: data?.status ?? "",
          })
        );
        navigate("/my-loans", { replace: true });
      } else {
        setError(0);
        if (data.user) {
          setFSUserProperties(getValues("email") as string, getLastNameLowercase(data.user.lastName));
        }
        dispatch(setResendCodeDeviceId(data.deviceId ?? ""));
        dispatch(setAuthDeviceType("email"));
        if (data.user) {
          dispatch(setUserId(data.user.id));
        }
        navigate("/registrationAuth", { replace: true });
      }
    }
  }, [isSuccess, data]);

  const ispasswordFieldModified = (dirtyFields.password_input as boolean) || false;
  const phoneValue = watch("phone") as string | undefined;
  const passwordValue = watch("password_input") as string | undefined;

  if (isRegistrationLoading || isDecryptLoading || isUserDetailsLoading) return <LoadingOverlay text="Loading..." />;

  const hasErrors = !!Object.keys(errors).length;
  const isPhoneValid = !isPhoneRequired || phoneValue?.length === 10;
  const isPasswordValid = (passwordValue?.length ?? 0) > 0 && !hasPwdError;
  const isBtnDisabled = !isPasswordValid || !isPhoneValid || hasErrors;

  const formatContent = (content: string[]) => {
    return (
      <ul className="registration-list">
        {content?.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <div className="registration">
        <div className="registration__container">
          <div className="registration__form">
            <h1 className="registration__header"> {isDesktop ? `Create an account to continue your application` : `Create an account to continue`}</h1>
            {error == 500 && (
              <div className="registration__error">
                {isDesktop
                  ? Constant.RegistrationError
                  : Constant.RegistrationError.map((error) => {
                    return <p key={error}>{error}</p>;
                  })}
              </div>
            )}
            {passwordRequestFailureMessage && <div className="registration__error">{passwordRequestFailureMessage}</div>}
            <Form
              onSubmit={handleSubmit((data) => {
                onSubmit({
                  email: data.email as string,
                  password: data.password_input as string,
                  nhDealApplicationId: nhDealApplicationId,
                  phoneNumber: data.phone as string,
                });
              })}
            >
              <div className="row--item">
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter Your Email Address",
                    label: "Email Address",
                    errorMessage: errors.email?.message as string,
                    isReadOnly: true,
                  }}
                />
                {!isPhoneNumberFetching && isPhoneRequired && isUserDetailsSuccess && (
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <PhoneNumberInput
                        onChange={(newValue: string) => {
                            onChange(newValue);
                            setValue("phone", schemas.phone.parse(newValue));
                        }}
                        value={value as string}
                        errorMessage={errors.phone?.message as string}
                        label="Phone Number"
                        placeholder={`Enter Phone Number`}
                      />
                    )}
                  />
                )}
              </div>
              <div className="row--item">
                <Controller
                  name="password_input"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <PasswordChecker
                      onChange={onChange}
                      checkErrors={ispasswordFieldModified}
                      createLabel="Create Password"
                      setHasPasswordError={setHasPwdError}
                      createPasswordPlaceholderName="Create password"
                    />
                  )}
                />
              </div>
              <Button title="Continue" className="registration__button" disabled={isBtnDisabled} />
            </Form>
            <Consent text={Constant.PRIVACY_AND_TERMSOFUSE_CONSENT} />
          </div>
          {isDesktop && (
            <div className="registration__information">
              <p className="registration__text1">Better Path to Homeownership</p>
              <p className="registration__text2">Seamless, Secure, and Built for You</p>
              <p className="registration__text3">{formatContent(Constant.registrationInformation)}</p>
              <img src={homeGraphic} alt="login-graphic" className="registration__graphic" />
            </div>
          )}
        </div>
      </div>
      <div className="ext-registration-footer">
        <Footer />
      </div>
    </>
  );
}
